<script setup lang="ts">
import { useSpecialIssueStore } from '@/stores/specialIssues'
import { useAuthStore } from '@/stores/auth'

const props = defineProps({
  formId: {
    type: Number,
    default: null,
  },
})

const $config = useRuntimeConfig()

const formidableId = computed(
  () => props.formId ?? $config.public.formidableForms.specialIssues,
)

const isGigafactoriesForm = computed(
  () =>
    parseInt(formidableId.value) ===
    parseInt($config.public.formidableForms.specialIssues),
)

const isDigitalCopy = computed(() => {
  return fields.value.some(
    (field) =>
      Array.isArray(field.value) &&
      field.value.includes('Digital Copy via email'),
  )
})

const isPhysicalCopy = computed(() => {
  return fields.value.some(
    (field) =>
      Array.isArray(field.value) && field.value.includes('Physical Copy'),
  )
})

const emailField = computed(() => {
  return fields.value.find((field) => field.label === 'Email').value
})

const specialIssueStore = useSpecialIssueStore()
const authStore = useAuthStore()
const { callToAction, isDownloading, downloadProgress, cancelDownload } =
  useDownloadFile()

const submittedForm = ref<boolean>(false)
const pageNumberForm = ref<number>(1)
const fields = ref([])

const specialIssue = computed(() => specialIssueStore.getSpecialIssue)

const onChangedPageNumber = (pageNumber: number) => {
  pageNumberForm.value = pageNumber
}

const onFormSubmit = (fieldsForm) => {
  fields.value = fieldsForm
  submittedForm.value = true
}

function download() {
  callToAction.value.callback(
    specialIssue.value.id,
    'special-issues',
    specialIssue.value.fileName,
  )
}
</script>

<template>
  <div class="flex max-w-[465px] flex-col text-base">
    <div v-if="submittedForm" class="text-center">
      <div
        v-if="isGigafactoriesForm"
        class="flex flex-col gap-6"
        :class="[authStore.isAuthenticated ? 'flex-col-reverse' : '']"
      >
        <div v-show="isDigitalCopy">
          Your copy of
          <b>{{ specialIssue.title }}.</b> has been emailed to
          <b>{{ emailField }}.</b>
        </div>
        <div v-show="isPhysicalCopy">
          Your physical copy will be sent out to you after review within the
          next two weeks
        </div>
      </div>
      <div v-else class="font-bold">
        Thank you for contacting us, one of our team will be in touch shortly.
      </div>
    </div>
    <div v-else>
      <div v-show="isGigafactoriesForm && pageNumberForm === 1" class="px-12">
        <p class="mb-6">
          If you would like to receive a copy of
          <b>{{ specialIssue.title }}</b> select from the options below.
        </p>
        <p v-show="!authStore.isAuthenticated">
          Already a subscriber?
          <NuxtLink to="/login" class="cursor-pointer font-bold underline">
            Sign in
          </NuxtLink>
        </p>
      </div>
      <div v-show="!isGigafactoriesForm">
        <p class="mb-6">
          If you would like to download a copy of
          {{ specialIssue.title }} please fill in the form.
        </p>
        <p>
          If you are a Benchmark Source subscriber please
          <NuxtLink to="/login" class="cursor-pointer font-bold underline">
            Sign in
          </NuxtLink>
        </p>
      </div>
      <div
        v-show="authStore.isAuthenticated"
        class="flex flex-col items-center justify-center"
      >
        Click below to download your digital copy
        <div class="mt-5 flex justify-center px-3">
          <CircularProgressBar
            v-if="isDownloading || downloadProgress"
            :progress="downloadProgress"
            :cancel-call-back="cancelDownload"
            :is-loading="isDownloading"
            @click="download"
          />
          <BaseButton
            v-else
            variant="primary"
            class="w-full"
            data-testid="call-to-action"
            @click="download"
          >
            {{ callToAction.text }}
          </BaseButton>
        </div>
      </div>
      <div :class="[isGigafactoriesForm ? 'min-h-[10vh]' : 'min-h-[60vh]']">
        <FormBuilder
          :form-id="formidableId"
          field-extra-classes="mb-1"
          full-width-to-button
          :show-custom-button="isGigafactoriesForm"
          text-button="Submit"
          :show-tab-section="!isGigafactoriesForm"
          @form-send-success="onFormSubmit"
          @page-number-change="onChangedPageNumber"
        />
      </div>
    </div>
  </div>
</template>
